import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Text,
  VStack,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  HStack,
} from '@chakra-ui/react';
import { influencerApi } from 'services/api/influencer';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { Pill } from '../../../components/ui';
import { ff } from '../../../theme';

interface SocialsEditProps {
  instagramHandle: string;
  /** Candidate handle the creator is typing. Deliberately separate from the live handle. */
  newInstagramHandle: string;
  setNewInstagramHandle: (handle: string) => void;
  /** Set while a handle change is in flight and not yet proven. */
  pendingInstagramHandle?: string;
  /** Epoch ms after which the pending handle's verification code stops being accepted. */
  pendingHandleExpiresAt?: number;
  isRequestingHandleChange: boolean;
  onRequestHandleChange: () => void;
  onCancelHandleChange: () => void;
  tiktokHandle: string;
  setTiktokHandle: (handle: string) => void;
  youtubeHandle: string;
  setYoutubeHandle: (handle: string) => void;
  pinterestHandle: string;
  setPinterestHandle: (handle: string) => void;
  snapchatHandle: string;
  setSnapchatHandle: (handle: string) => void;
  rateRanges: any;
  setRateRanges: (ranges: any) => void;
  lastSavedSocialsInfo: {
    tiktokHandle: string;
    youtubeHandle: string;
    pinterestHandle: string;
    snapchatHandle: string;
  } | null;
  onSocialsSaved: (field: string, value: string) => void;
  onBack: () => void;
  /** null = no proof record either way; show nothing rather than a warning. */
  isInstagramVerified: boolean | null;
  isCheckingInstagramVerified: boolean;
  onVerifyInstagram: () => void;
}

/** Seconds left on a verification code, or undefined when nothing is pending. */
const useCodeCountdown = (expiresAt?: number): number | undefined => {
  const [remaining, setRemaining] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!expiresAt) {
      setRemaining(undefined);
      return;
    }

    const tick = () => setRemaining(Math.max(0, Math.round((expiresAt - Date.now()) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  return remaining;
};

const formatCountdown = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
};

const SocialsEdit: React.FC<SocialsEditProps> = ({
  instagramHandle,
  newInstagramHandle,
  setNewInstagramHandle,
  pendingInstagramHandle,
  pendingHandleExpiresAt,
  isRequestingHandleChange,
  onRequestHandleChange,
  onCancelHandleChange,
  tiktokHandle,
  setTiktokHandle,
  youtubeHandle,
  setYoutubeHandle,
  pinterestHandle,
  setPinterestHandle,
  snapchatHandle,
  setSnapchatHandle,
  rateRanges,
  setRateRanges,
  lastSavedSocialsInfo,
  onSocialsSaved,
  onBack,
  isInstagramVerified,
  isCheckingInstagramVerified,
  onVerifyInstagram,
}) => {
  const toast = useToast();
  const { influencerData } = useContext(InfluencerDataContext);
  const remainingCodeTime = useCodeCountdown(pendingHandleExpiresAt);
  const isCodeExpired = !!pendingInstagramHandle && remainingCodeTime === 0;
  
  // Per-field saving state flags
  const [isSavingTiktok, setIsSavingTiktok] = useState(false);
  const [isSavingYoutube, setIsSavingYoutube] = useState(false);
  const [isSavingPinterest, setIsSavingPinterest] = useState(false);
  const [isSavingSnapchat, setIsSavingSnapchat] = useState(false);

  /**
   * Autosave helper for social handles
   * Follows the same pattern as PersonalInformationEdit
   */
  const autosaveField = async (
    fieldKey: 'tiktokHandle' | 'youtubeHandle' | 'pinterestHandle' | 'snapchatHandle',
    value: string,
    setSavingFlag: (value: boolean) => void,
    fieldDisplayName: string
  ) => {
    if (!lastSavedSocialsInfo || !influencerData?.id) return;
    
    try {
      setSavingFlag(true);
      
      const hasInstagramHandle = instagramHandle?.trim() !== '';
      const hasTiktokHandle = fieldKey === 'tiktokHandle' ? value.trim() !== '' : tiktokHandle?.trim() !== '';
      
      const updatedRateRanges = {
        ...rateRanges,
        instagramPosts: rateRanges?.instagramPosts
          ? { ...rateRanges.instagramPosts, offered: hasInstagramHandle }
          : rateRanges?.instagramPosts,
        instagramReels: rateRanges?.instagramReels
          ? { ...rateRanges.instagramReels, offered: hasInstagramHandle }
          : rateRanges?.instagramReels,
        instagramStories: rateRanges?.instagramStories
          ? { ...rateRanges.instagramStories, offered: hasInstagramHandle }
          : rateRanges?.instagramStories,
        tiktoks: rateRanges?.tiktoks
          ? { ...rateRanges.tiktoks, offered: hasTiktokHandle }
          : rateRanges?.tiktoks,
      };
      
      setRateRanges(updatedRateRanges);
      
      await influencerApi.updateProfile({
        [fieldKey]: value,
        rateRanges: updatedRateRanges
      }, influencerData.id);
      
      // Update last saved value on success
      onSocialsSaved(fieldKey, value);
    } catch (error) {
      console.error(`Error autosaving ${fieldKey}:`, error);
      // Revert to last saved value
      const lastSavedValue = lastSavedSocialsInfo[fieldKey];
      if (fieldKey === 'tiktokHandle') setTiktokHandle(lastSavedValue);
      else if (fieldKey === 'youtubeHandle') setYoutubeHandle(lastSavedValue);
      else if (fieldKey === 'pinterestHandle') setPinterestHandle(lastSavedValue);
      else if (fieldKey === 'snapchatHandle') setSnapchatHandle(lastSavedValue);
      
      toast({
        title: `Couldn't save ${fieldDisplayName}`,
        description: "Reverted to last saved value",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSavingFlag(false);
    }
  };

  return (
    <Box>
      {/* Description */}
      <Text color={ff.text2} fontSize="14px" mb={5}>
        Manage your connected social media accounts
      </Text>

      {/* Form */}
      <VStack spacing={4} align="stretch" mb={6}>
        {/* Instagram Section */}
        <FormControl border="1px solid" borderColor={ff.line} borderRadius="20px" p={4}>
          <Flex alignItems="center" mb={3}>
            <FormLabel mb="0" textStyle="display" fontSize="18px">
              Instagram
            </FormLabel>
          </Flex>
          <FormControl>
            <FormLabel>Instagram handle</FormLabel>
            {/* Not autosaved on blur like the handles below: a typed handle only becomes a
                pending change on the button press, and only goes live once proven by DM. */}
            <InputGroup>
              <Input
                placeholder="@username"
                value={newInstagramHandle ? `@${newInstagramHandle.replace('@', '')}` : ''}
                onChange={(e) => setNewInstagramHandle(e.target.value.replace('@', ''))}
                isDisabled={isRequestingHandleChange || !!pendingInstagramHandle}
              />
              {isRequestingHandleChange && (
                <InputRightElement width="3rem">
                  <Spinner size="sm" color={ff.muted} />
                </InputRightElement>
              )}
            </InputGroup>

            {instagramHandle && (
              <Flex alignItems="center" mt={2} gap={2}>
                <Text fontSize="14px" color={ff.text2}>
                  Current: <Text as="span" fontWeight={600} color={ff.ink}>@{instagramHandle}</Text>
                </Text>
                {!isCheckingInstagramVerified && isInstagramVerified && (
                  <Pill tone="lime">✓ Verified</Pill>
                )}
                {!isCheckingInstagramVerified && isInstagramVerified === false && (
                  <Pill tone="grey">Not verified</Pill>
                )}
              </Flex>
            )}

            {!pendingInstagramHandle && (
              <Button
                mt={3}
                variant="ink"
                h="44px"
                px={5}
                isLoading={isRequestingHandleChange}
                isDisabled={
                  !newInstagramHandle.trim()
                  || newInstagramHandle.trim().replace('@', '').toLowerCase() === instagramHandle?.toLowerCase()
                }
                onClick={onRequestHandleChange}
              >
                Change &amp; verify
              </Button>
            )}
            <Text mt={2} fontSize="12px" color={ff.text2}>
              Your current handle stays live until you prove you own the new one.
            </Text>
          </FormControl>

          {pendingInstagramHandle && (
            <Alert
              status={isCodeExpired ? 'warning' : 'info'}
              bg={isCodeExpired ? ff.cream : ff.limeSurface}
              color={ff.ink}
              borderRadius="14px"
              mt={3}
              py={3}
              alignItems="flex-start"
            >
              <AlertIcon color={ff.ink} />
              <Box flex="1">
                <AlertTitle fontSize="14px" fontWeight={600}>
                  {isCodeExpired
                    ? `Verification for @${pendingInstagramHandle} expired`
                    : `Waiting for a DM from @${pendingInstagramHandle}`}
                </AlertTitle>
                <AlertDescription fontSize="13px" color={ff.body} display="block">
                  {isCodeExpired
                    ? 'Codes are only valid for 10 minutes. Cancel and start again to get a new one.'
                    : 'Send the code from your new account. This page updates itself once we see it.'}
                </AlertDescription>
                <HStack spacing={3} mt={2}>
                  {!isCodeExpired && (
                    <Button variant="ink" h="44px" px={4} fontSize="14px" onClick={onVerifyInstagram}>
                      Show code
                    </Button>
                  )}
                  <Button variant="inkOutline" h="44px" px={4} fontSize="14px" onClick={onCancelHandleChange}>
                    Cancel
                  </Button>
                  {!isCodeExpired && remainingCodeTime !== undefined && (
                    <Text fontSize="12px" color={ff.text2}>
                      Expires in {formatCountdown(remainingCodeTime)}
                    </Text>
                  )}
                </HStack>
              </Box>
            </Alert>
          )}

          {!pendingInstagramHandle && instagramHandle && !isCheckingInstagramVerified && isInstagramVerified === false && (
            <Alert status="warning" bg={ff.cream} color={ff.ink} borderRadius="14px" mt={3} py={3}>
              <AlertIcon color={ff.ink} />
              <Box flex="1">
                <AlertTitle fontSize="14px" fontWeight={600}>Instagram not verified</AlertTitle>
                <AlertDescription fontSize="13px" color={ff.body}>
                  Verify your account to increase visibility to brands.
                </AlertDescription>
              </Box>
              <Button variant="ink" h="44px" px={4} fontSize="14px" onClick={onVerifyInstagram}>
                Verify
              </Button>
            </Alert>
          )}
        </FormControl>

        {/* TikTok Section */}
        <FormControl border="1px solid" borderColor={ff.line} borderRadius="20px" p={4}>
          <Flex alignItems="center" mb={3}>
            <FormLabel mb="0" textStyle="display" fontSize="18px">
              TikTok
            </FormLabel>
          </Flex>
          <FormControl>
            <FormLabel>TikTok handle</FormLabel>
            <InputGroup>
              <Input 
                placeholder="@username" 
                value={tiktokHandle ? `@${tiktokHandle.replace('@', '')}` : '@'} 
                onChange={(e) => setTiktokHandle(e.target.value.replace('@', ''))}
                onBlur={() => {
                  if (lastSavedSocialsInfo && tiktokHandle !== lastSavedSocialsInfo.tiktokHandle) {
                    autosaveField('tiktokHandle', tiktokHandle, setIsSavingTiktok, 'TikTok Handle');
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (lastSavedSocialsInfo && tiktokHandle !== lastSavedSocialsInfo.tiktokHandle) {
                      autosaveField('tiktokHandle', tiktokHandle, setIsSavingTiktok, 'TikTok Handle');
                    }
                  }
                }}
                pr={isSavingTiktok ? '3rem' : undefined}
              />
              {isSavingTiktok && (
                <InputRightElement width="3rem">
                  <Spinner size="sm" color={ff.muted} />
                </InputRightElement>
              )}
            </InputGroup>
          </FormControl>
        </FormControl>

        {/* YouTube Section */}
        <FormControl border="1px solid" borderColor={ff.line} borderRadius="20px" p={4}>
          <Flex alignItems="center" mb={3}>
            <FormLabel mb="0" textStyle="display" fontSize="18px">
              YouTube
            </FormLabel>
          </Flex>
          <FormControl>
            <FormLabel>YouTube handle</FormLabel>
            <InputGroup>
              <Input 
                placeholder="@username" 
                value={youtubeHandle ? `@${youtubeHandle.replace('@', '')}` : '@'} 
                onChange={(e) => setYoutubeHandle(e.target.value.replace('@', ''))}
                onBlur={() => {
                  if (lastSavedSocialsInfo && youtubeHandle !== lastSavedSocialsInfo.youtubeHandle) {
                    autosaveField('youtubeHandle', youtubeHandle, setIsSavingYoutube, 'YouTube Handle');
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (lastSavedSocialsInfo && youtubeHandle !== lastSavedSocialsInfo.youtubeHandle) {
                      autosaveField('youtubeHandle', youtubeHandle, setIsSavingYoutube, 'YouTube Handle');
                    }
                  }
                }}
                pr={isSavingYoutube ? '3rem' : undefined}
              />
              {isSavingYoutube && (
                <InputRightElement width="3rem">
                  <Spinner size="sm" color={ff.muted} />
                </InputRightElement>
              )}
            </InputGroup>
          </FormControl>
        </FormControl>

        {/* Pinterest Section */}
        <FormControl border="1px solid" borderColor={ff.line} borderRadius="20px" p={4}>
          <Flex alignItems="center" mb={3}>
            <FormLabel mb="0" textStyle="display" fontSize="18px">
              Pinterest
            </FormLabel>
          </Flex>
          <FormControl>
            <FormLabel>Pinterest handle</FormLabel>
            <InputGroup>
              <Input 
                placeholder="@username" 
                value={pinterestHandle ? `@${pinterestHandle.replace('@', '')}` : '@'} 
                onChange={(e) => setPinterestHandle(e.target.value.replace('@', ''))}
                onBlur={() => {
                  if (lastSavedSocialsInfo && pinterestHandle !== lastSavedSocialsInfo.pinterestHandle) {
                    autosaveField('pinterestHandle', pinterestHandle, setIsSavingPinterest, 'Pinterest Handle');
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (lastSavedSocialsInfo && pinterestHandle !== lastSavedSocialsInfo.pinterestHandle) {
                      autosaveField('pinterestHandle', pinterestHandle, setIsSavingPinterest, 'Pinterest Handle');
                    }
                  }
                }}
                pr={isSavingPinterest ? '3rem' : undefined}
              />
              {isSavingPinterest && (
                <InputRightElement width="3rem">
                  <Spinner size="sm" color={ff.muted} />
                </InputRightElement>
              )}
            </InputGroup>
          </FormControl>
        </FormControl>

        {/* Snapchat Section */}
        <FormControl border="1px solid" borderColor={ff.line} borderRadius="20px" p={4}>
          <Flex alignItems="center" mb={3}>
            <FormLabel mb="0" textStyle="display" fontSize="18px">
              Snapchat
            </FormLabel>
          </Flex>
          <FormControl>
            <FormLabel>Snapchat handle</FormLabel>
            <InputGroup>
              <Input 
                placeholder="@username" 
                value={snapchatHandle ? `@${snapchatHandle.replace('@', '')}` : '@'} 
                onChange={(e) => setSnapchatHandle(e.target.value.replace('@', ''))}
                onBlur={() => {
                  if (lastSavedSocialsInfo && snapchatHandle !== lastSavedSocialsInfo.snapchatHandle) {
                    autosaveField('snapchatHandle', snapchatHandle, setIsSavingSnapchat, 'Snapchat Handle');
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (lastSavedSocialsInfo && snapchatHandle !== lastSavedSocialsInfo.snapchatHandle) {
                      autosaveField('snapchatHandle', snapchatHandle, setIsSavingSnapchat, 'Snapchat Handle');
                    }
                  }
                }}
                pr={isSavingSnapchat ? '3rem' : undefined}
              />
              {isSavingSnapchat && (
                <InputRightElement width="3rem">
                  <Spinner size="sm" color={ff.muted} />
                </InputRightElement>
              )}
            </InputGroup>
          </FormControl>
        </FormControl>
      </VStack>

      {/* Saving Indicator */}
      {(isSavingTiktok || isSavingYoutube || isSavingPinterest || isSavingSnapchat) && (
        <Flex justify="center" align="center" py={4}>
          <Spinner size="sm" color={ff.muted} mr={2} />
          <Text fontSize="13px" color={ff.text2}>Saving...</Text>
        </Flex>
      )}
    </Box>
  );
};

export default SocialsEdit;